import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

/**
 * Hook do sprawdzania ustawień prywatności użytkownika
 * @returns {Object} { hideLastActivity, disableActivityNotifications, disableProfileFollowing, isLoading, error, refetch }
 */
export function useUserPrivacySettings() {
  const [hideLastActivity, setHideLastActivity] = useState(false);
  const [disableActivityNotifications, setDisableActivityNotifications] = useState(false);
  const [disableProfileFollowing, setDisableProfileFollowing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState(null);

  const fetchPrivacySettings = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        // Brak użytkownika - ustaw domyślne wartości
        setHideLastActivity(false);
        setDisableActivityNotifications(false);
        setDisableProfileFollowing(false);
        setUserId(null);
        setIsLoading(false);
        return;
      }

      setUserId(user.id);

      // Pobierz ustawienia prywatności z public.users
      const { data: profile, error: profileError } = await supabase
        .from('users')
        .select('hide_last_activity, disable_activity_notifications, disable_profile_following')
        .eq('id', user.id)
        .single();

      if (profileError) {
        throw profileError;
      }

      // Ustaw wartości (domyślnie false jeśli null)
      setHideLastActivity(profile?.hide_last_activity === true);
      setDisableActivityNotifications(profile?.disable_activity_notifications === true);
      setDisableProfileFollowing(profile?.disable_profile_following === true);
    } catch (err) {
      console.error('Error fetching privacy settings:', err);
      setError(err.message);
      // W przypadku błędu, ustaw domyślne wartości (fail-safe)
      setHideLastActivity(false);
      setDisableActivityNotifications(false);
      setDisableProfileFollowing(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPrivacySettings();

    // BroadcastChannel - synchronizacja między oknami tej samej przeglądarki
    let broadcastChannel = null;
    if (typeof BroadcastChannel !== 'undefined' && userId) {
      broadcastChannel = new BroadcastChannel('privacy_settings_sync');
      
      broadcastChannel.onmessage = (event) => {
        const { type, userId: messageUserId, field, value } = event.data;
        
        // Ignoruj wiadomości z innych użytkowników
        if (type === 'privacy_updated' && messageUserId === userId && field && value !== undefined) {
          // Inne okno zmieniło ustawienie - zaktualizuj cache bezpośrednio (bez fetchPrivacySettings)
          switch (field) {
            case 'hide_last_activity':
              setHideLastActivity(value === true);
              break;
            case 'disable_activity_notifications':
              setDisableActivityNotifications(value === true);
              break;
            case 'disable_profile_following':
              setDisableProfileFollowing(value === true);
              break;
            default:
              // Fallback - jeśli nie rozpoznajemy pola, odśwież wszystko
              fetchPrivacySettings();
          }
        }
      };
    }

    // Nasłuchuj zmian w autoryzacji (np. wylogowanie, ponowne zalogowanie)
    const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user?.id !== userId) {
        // Tylko jeśli zmienił się użytkownik
        fetchPrivacySettings();
      }
    });

    return () => {
      if (broadcastChannel) {
        broadcastChannel.close();
      }
      authListener.subscription.unsubscribe();
    };
  }, [fetchPrivacySettings, userId]);

  return { 
    hideLastActivity, 
    disableActivityNotifications, 
    disableProfileFollowing, 
    isLoading, 
    error, 
    refetch: fetchPrivacySettings 
  };
}

export default useUserPrivacySettings;

